import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RegularButton, Typography } from 'fronton-react';
import { cx } from 'linaria';
import { YaSplitInfo } from 'types/page-model';
import { getIsBot } from 'common/selectors';
import {
  getCurrency,
  getDivisionId,
  getIsBoosterAvailable,
  getSource,
  isCalculatorAvailable,
} from 'features/product-info';
import { getIsGoodSupplyLoading, getStocksInStores } from 'features/goods-supply';
import { analyticsUtils, getLocale } from 'utils';
import {
  yandexSplitContainer,
  textContainer,
  withPrice,
  smallPadding,
  yandexSplitLogo,
  additionalMarginTop,
  additionalMarginBottom,
} from './styles';
import { formatPrice } from './utils';
import { YandexSplitBannerSkeleton } from './yandex-split-banner-skeleton';
import { createGetExperiment, getIsPdpBoosterExperiment } from 'features/page-model';
import { HIDE_CALCULATOR_INTERNAL_ID } from 'common/constants';

const xSymbol = 'х';

export type Props = {
  yaSplitInfo: YaSplitInfo;
  price?: number | null;
  currency?: string;
  isSet?: boolean;
  showCatchPushPromo?: boolean;
};

export const YandexSplitBanner: React.FC<Props> = ({
  yaSplitInfo: {
    enabled,
    mainText,
    priceBreakThreshold,
    maxPriceThreshold,
    splitCount,
    linkPage: { href, target },
    logoLink,
    KindDivisionsList,
    KindSourceList,
  },
  price,
  isSet,
  showCatchPushPromo,
}) => {
  const isSentAnalytics = useRef(false);
  const isBot = useSelector(getIsBot);
  const currentDivision = String(useSelector(getDivisionId));
  const currentSource = useSelector(getSource)?.toLowerCase();
  const currency = useSelector(getCurrency) || 'RUB';
  const stocks = useSelector(getStocksInStores);
  const isStocksLoading = useSelector(getIsGoodSupplyLoading);
  const isCalcHidden = useSelector(createGetExperiment(HIDE_CALCULATOR_INTERNAL_ID));
  const isCalcAvailable = (useSelector(isCalculatorAvailable) && !isCalcHidden) ?? false;
  const isShowBooster = useSelector(getIsBoosterAvailable) ?? false;
  const isPdpBoosterExperimentAvailable = useSelector(getIsPdpBoosterExperiment);
  const isBoosterAvailable = isShowBooster && isPdpBoosterExperimentAvailable;
  const isShowBoosterButton = !isCalcAvailable && isBoosterAvailable;
  const showPrice = splitCount && price && priceBreakThreshold && price >= priceBreakThreshold;

  let splitPriceFormatted: string | undefined;
  const locale = getLocale();

  if (showPrice) {
    const splitPrice = price / splitCount;
    splitPriceFormatted = formatPrice(splitPrice, currency, locale);
  }

  const analyticsData = useMemo(
    () => ({
      elementName: 'Яндекс.Сплит',
      elementText: `${splitPriceFormatted} ${xSymbol} ${splitCount}`,
      elementType: 'banner',
      elementBlock: isSet ? 'komplektyDetail' : 'productDetail',
    }),
    [splitPriceFormatted, splitCount, isSet],
  );

  const getVisibilityBanner = () => {
    if (isBot || !enabled) return false;
    if (!mainText) return false;

    if (!stocks.length && !isStocksLoading && !isSet) return false;

    if (price && maxPriceThreshold && price > maxPriceThreshold) return false;

    const isExcludedDivision = KindDivisionsList.some((d) => String(d) === currentDivision);
    const isExcludedSource = KindSourceList.some((s) => s.toLowerCase() === currentSource);
    if (isExcludedDivision || isExcludedSource) return false;

    return true;
  };

  const shouldShowBanner = getVisibilityBanner();

  useEffect(() => {
    if (shouldShowBanner && !isStocksLoading && !isSentAnalytics.current) {
      analyticsUtils.sendEvent('GA:show', analyticsData);
      isSentAnalytics.current = true;
    }
  }, [shouldShowBanner, isStocksLoading, analyticsData]);

  const handleClick = () => {
    analyticsUtils.sendEvent('GA:clickButton', analyticsData);
  };

  if (!shouldShowBanner) return null;

  if (isStocksLoading) return <YandexSplitBannerSkeleton />;

  const logoSize = showPrice ? 20 : 16;

  const yandexSplitContainerStyleSet = cx(yandexSplitContainer, isSet ? additionalMarginTop : null);

  const yandexSplitContainerStyle = cx(
    yandexSplitContainerStyleSet,
    (isCalcAvailable || isShowBoosterButton) && !showCatchPushPromo ? additionalMarginBottom : null,
  );

  return (
    <RegularButton
      target={target ?? '_blank'}
      href={href}
      onClick={handleClick}
      variant="secondary"
      className={cx(yandexSplitContainerStyle, !showPrice ? smallPadding : null)}
    >
      <img className={yandexSplitLogo} src={logoLink} alt={mainText} width={logoSize} height={logoSize} />
      <div className={showPrice ? cx(textContainer, withPrice) : textContainer}>
        {showPrice && (
          <Typography color="text-primary" size="body_accent" variant="s">
            {splitPriceFormatted} {xSymbol} {splitCount}
          </Typography>
        )}
        <Typography color="text-primary" size="caption" variant="minor">
          {mainText}
        </Typography>
      </div>
    </RegularButton>
  );
};
